/**
 * SE｜モデルスライダー 自動スクロール（sections/se-model-slider.liquid）
 *
 * なぜ CSS だけで完結しないか:
 *   見た目のループだけなら transform + @keyframes の marquee で足りるが、
 *   それだとトラックがスクロールコンテナでなくなり、ネイティブのスワイプ（慣性つき）が失われる。
 *   「手動でスワイプしたら止まり、やめたら再開する」を満たすには
 *   スクロール位置を JS で進め、ユーザー操作を検知して止める必要がある。
 *   ライブラリは使わず、scrollLeft を rAF で進めるだけにしている。
 *
 * 切れ目を見せない方法:
 *   Liquid 側が同じ並びを2周ぶん出力している（2周目は aria-hidden + data-se-clone）。
 *   1周ぶん（トラック幅の半分）進んだら scrollLeft から半分を引く。
 *   同じ画像が並んでいるので、巻き戻しは見えない。
 *
 * 一時停止:
 *   pointerdown / touchstart / wheel / キー操作 → 即停止し、操作が止まって一定時間後に再開
 *   hover / focusin → 停止、離れたら再開
 *   画面外（IntersectionObserver）→ 停止
 *   prefers-reduced-motion: reduce → 自動再生しない
 *
 * scroll-snap:
 *   自動再生中は scroll-snap が働くと 1px ずつの移動がスナップに戻されるため、
 *   再生中だけ .is-autoplaying を付けて snap を無効化する（手動時は snap が効く）。
 */
if (!customElements.get('se-model-slider')) {
  customElements.define(
    'se-model-slider',
    class SeModelSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-se-track]');
        if (!this.track || !this.hasAttribute('data-se-autoplay')) return;

        this.speed = Number(this.dataset.seSpeed) || 25; // px/秒
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.paused = true;
        this.visible = false;
        this.pointerHeld = false;
        this.hovered = false;
        this.focused = false;
        this.lastTime = 0;
        this.resumeTimer = 0;

        this.onFrame = this.onFrame.bind(this);

        // ユーザー操作 → 停止し、操作が途切れたら再開
        this.onInteract = () => {
          this.pause();
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => {
            this.pointerHeld = false;
            this.maybePlay();
          }, 1200);
        };
        this.onPointerDown = () => {
          this.pointerHeld = true;
          this.onInteract();
        };
        this.onPointerUp = () => {
          this.onInteract();
        };

        this.onEnter = () => {
          this.hovered = true;
          this.pause();
        };
        this.onLeave = () => {
          this.hovered = false;
          this.maybePlay();
        };
        this.onFocusIn = () => {
          this.focused = true;
          this.pause();
        };
        this.onFocusOut = () => {
          this.focused = false;
          this.maybePlay();
        };

        this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
        this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
        this.track.addEventListener('touchstart', this.onInteract, { passive: true });
        this.track.addEventListener('touchend', this.onInteract, { passive: true });
        this.track.addEventListener('wheel', this.onInteract, { passive: true });
        this.track.addEventListener('keydown', this.onInteract);

        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);

        // 画面外では動かさない
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            if (this.visible) {
              this.maybePlay();
            } else {
              this.pause();
            }
          },
          { threshold: 0 }
        );
        this.observer.observe(this);

        this.onMotionChange = () => {
          if (this.reducedMotion.matches) {
            this.pause();
          } else {
            this.maybePlay();
          }
        };
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      }

      disconnectedCallback() {
        this.pause();
        clearTimeout(this.resumeTimer);
        this.observer?.disconnect();
        this.reducedMotion?.removeEventListener('change', this.onMotionChange);
      }

      maybePlay() {
        if (!this.paused) return;
        if (this.reducedMotion.matches) return;
        if (!this.visible || this.hovered || this.focused || this.pointerHeld) return;

        this.paused = false;
        this.classList.add('is-autoplaying');
        this.lastTime = 0;
        // 手動スクロール後の位置から続ける。
        // scrollLeft は代入時に丸められることがあるので、進捗は小数で自前に持つ
        this.position = this.track.scrollLeft;
        this.frame = requestAnimationFrame(this.onFrame);
      }

      pause() {
        if (this.paused) return;
        this.paused = true;
        this.classList.remove('is-autoplaying');
        cancelAnimationFrame(this.frame);
      }

      onFrame(now) {
        if (this.paused) return;

        if (this.lastTime) {
          // 1周ぶん = トラック全体の半分（Liquid が同じ並びを2周ぶん出力している）
          const loopWidth = this.track.scrollWidth / 2;
          const delta = ((now - this.lastTime) / 1000) * this.speed;

          // タブ復帰などで大きく飛んだフレームは捨てる
          if (delta > 0 && delta < 50 && loopWidth > 0) {
            this.position += delta;
            if (this.position >= loopWidth) this.position -= loopWidth;
            this.track.scrollLeft = this.position;
          }
        }

        this.lastTime = now;
        this.frame = requestAnimationFrame(this.onFrame);
      }
    }
  );
}
